import {
    getRangeLabels,
    getTimeLineLabels,
    TimeSeriesDisableRange,
    TimeSeriesSessionLabelDataItem,
} from 'utils/timeseries/TimeSeriesDisableRange';
import { TimeSeriesDisableRangeLabel } from 'utils/timeseries/TimeSeriesDisableRange';
import { PortfolioReturnsDataState } from '../store/stream/PortfolioReturnsStreamState';
import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import { PortfolioReturnsComponentState } from '../store/component/PortfolioReturnsComponentState';
import { PortfolioReturnsDataPortfolioValues } from '../store/stream/PortfolioReturnsDataPortfolioValues';
import { PortfolioReturnsDataInitPortfolioValues } from '../store/stream/PortfolioReturnsDataInitPortfolioValues';
import { TimeLineLabel } from 'utils/components/TimeSeriesChart/timeline';
import { extentLinear } from '@d3fc/d3fc-extent';
import { timeUtils } from '@instinet-analytics/core-lib';

const { roundToDays, toDateTime } = timeUtils;

export interface PortfolioReturnsDataItem
    extends TimeSeriesSessionLabelDataItem {
    /*
     * 1 minute after time
     */
    endOfBucketTime: number;
    /*
     * Total bps value
     */
    totalBpsValue?: number;
    /*
     * 'Buy' bps return value ('b1' in Indigo1)
     */
    buyBpsValue?: number;
    /*
     * 'Sell/Short' or 'Sell' bps return value (if displayed)  ('b2' in Indigo1)
     */
    sellBpsValue?: number;
    /*
     * 'Short' bps return value (if displayed)  ('b3' in Indigo1)
     */
    shortBpsValue?: number;
    /*
     * Index1 bps return value  ('b4' in Indigo1)
     */
    index1BpsValue?: number;
    /*
     * Index2 bps return value ('b5' in Indigo1)
     */
    index2BpsValue?: number;
    /*
     * Total value change
     */
    totalValueChange?: number;
    /*
     * 'Buy' value change
     */
    buyValueChange?: number;
    /*
     * 'Sell/Short' or 'Sell' bps value change
     */
    sellValueChange?: number;
    /*
     * 'Short' bps value change
     */
    shortValueChange?: number;
    /*
     * Index1 bps value change
     */
    index1ValueChange?: number;
    /*
     * Index2 bps value change
     */
    index2ValueChange?: number;
    maxAbsoluteValueChange?: number;
}

export type PortfolioReturnsReferenceDataItemSource =
    | 'Empty'
    | 'Pnc'
    | 'InitValues'
    | 'TimeSeries'
    | 'AverageExecutionPrice';
/*
 * Base portfolio "bp" values used as referenve value for portfolio returns calculations (bp0-bp5 in Indigo1)
 */

export interface PortfolioReturnsReferenceDataItem {
    referenceSource: PortfolioReturnsReferenceDataItemSource;
    time?: number;
    totalGross?: number;
    totalNet?: number;
    buy?: number; // p1
    sell?: number; // p2
    short?: number; // p3
    index1?: number; // p4
    index2?: number; // p5
}

export interface PortfolioReturnsData {
    timeSeries: PortfolioReturnsDataItem[];
    breaksInTrading: TimeSeriesDisableRange[];
    rangeLabels: TimeSeriesDisableRangeLabel[];
    timeLineLabels: TimeLineLabel[];
    baseValues: PortfolioReturnsReferenceDataItem;
    availableBasisDays: Date[];
    /*
     * Indication of the value sizes, used to set legends widths and toolbar button positions
     */
    valuesSize: 'values-normal' | 'values-large' | 'values-extra-large';
    multipleSides: boolean;
}

export const initPortfolioReturnsData: PortfolioReturnsData = {
    timeSeries: [],
    breaksInTrading: [],
    rangeLabels: [],
    timeLineLabels: [],
    baseValues: { referenceSource: 'Empty' },
    availableBasisDays: [],
    valuesSize: 'values-normal',
    multipleSides: true,
};

export function buildPortfolioReturnsData(
    component: PortfolioReturnsComponentState,
    showBuysAsPositive: boolean,
    stream: PortfolioReturnsDataState | undefined,
    invertSellLine: boolean
): PortfolioReturnsData {
    if (
        !stream ||
        !stream.timeSeries ||
        stream.timeSeries.length === 0 ||
        !stream.portfolioValues ||
        stream.portfolioValues.length === 0
    ) {
        return initPortfolioReturnsData;
    }

    const availableBasisDays = getAvailableBasisDays(stream);
    const baseValues = mapBaseValues(component, stream, availableBasisDays);

    if (!baseValues) {
        return initPortfolioReturnsData;
    }

    const timeSeries = mapTimeSeries(
        stream,
        baseValues,
        showBuysAsPositive,
        invertSellLine
    );

    const haveBuys = timeSeries.find((t) => t.buyBpsValue !== undefined);
    const haveSells = timeSeries.find((t) => t.sellBpsValue !== undefined);
    const haveShorts = timeSeries.find((t) => t.shortBpsValue !== undefined);
    const multipleSides =
        (haveBuys ? 1 : 0) + (haveSells ? 1 : 0) + (haveShorts ? 1 : 0) > 1;

    const maxAbsoluteValueChange = Math.max(
        ...timeSeries
            .filter((t) => t.maxAbsoluteValueChange !== undefined)
            .map((t) => t.maxAbsoluteValueChange!)
    );

    const LargeThreshold = 100000000;
    const ExtraLargeThreshold = 10000000000;

    const valuesSize =
        maxAbsoluteValueChange >= ExtraLargeThreshold
            ? 'values-extra-large'
            : maxAbsoluteValueChange >= LargeThreshold
            ? 'values-large'
            : 'values-normal';

    const rangeLabels = getRangeLabels(stream.timeSeries, stream.disableRange);
    const timeLineLabels = getTimeLineLabels(
        stream.timeSeries,
        stream.disableRange,
        timeSeries
    );

    return {
        availableBasisDays,
        timeSeries,
        breaksInTrading: stream.disableRange,
        rangeLabels,
        timeLineLabels,
        baseValues,
        valuesSize,
        multipleSides,
    };
}

export function getAvailableBasisDays(stream: PortfolioReturnsDataState) {
    const pncTimeStamps = stream.portfolioValues.map(
        (p) => p.previousNightCloseDate
    );
    const timeSeriesTimeStamps = stream.timeSeries.map((t) => t._i * 60000);

    const dates = uniq(
        [...pncTimeStamps, ...timeSeriesTimeStamps].map((t) => roundToDays(t))
    );

    return dates.sort().map((d) => new Date(d));
}

export function mapBaseValues(
    component: PortfolioReturnsComponentState,
    stream: PortfolioReturnsDataState,
    availableBasisDays: Date[]
): PortfolioReturnsReferenceDataItem | undefined {
    const valuesToBase: (
        referenceSource: PortfolioReturnsReferenceDataItemSource,
        time: number,
        values: number[]
    ) => PortfolioReturnsReferenceDataItem = (
        referenceSource,
        time,
        values
    ) => ({
        referenceSource,
        time,
        // Note: values[0] was for the total - no longer used
        buy: values[1],
        sell: values[2],
        short: values[3],
        index1: values[4],
        index2: values[5],
    });

    const pncToBase: (
        pnc: PortfolioReturnsDataPortfolioValues
    ) => PortfolioReturnsReferenceDataItem = (pnc) =>
        valuesToBase('Pnc', pnc.previousNightCloseDate, pnc.values);

    const initValuesToBase: (
        init: PortfolioReturnsDataInitPortfolioValues
    ) => PortfolioReturnsReferenceDataItem = (init) =>
        valuesToBase('InitValues', init.maxTradingDateTimestamp, init.values);

    const sortedPncs = sortBy(stream.portfolioValues, 'previousNightCloseDate');
    const latestPnc = sortedPncs[stream.portfolioValues.length - 1];
    const latestPncBaseValues: PortfolioReturnsReferenceDataItem =
        pncToBase(latestPnc);

    let ret: PortfolioReturnsReferenceDataItem | undefined = undefined;

    const { avgExecRefValues } = component;

    if (avgExecRefValues) {
        // If the average execution base values are set, we use those...
        ret = {
            referenceSource: 'AverageExecutionPrice',
            buy: avgExecRefValues.buy,
            sell: avgExecRefValues.sell,
            short: avgExecRefValues.short,
            index1: undefined,
            index2: undefined,
            time: undefined,
        };
    } else if (isNil(component.basisDay) && isNil(component.basisTime)) {
        //  If BasisDate and BasisTime not set, use PNCS object with latest PNCS["t"] timestamp.
        ret = latestPncBaseValues;
    } else if (isNil(component.basisTime)) {
        // If BasisDate set and BasisTime not set, use PNCs where date string matches the BasisDate date string.

        const pnc = sortedPncs.find(
            (p) => p.previousNightCloseDate === component.basisDay!.getTime()
        );

        if (pnc) {
            ret = pncToBase(pnc);
        } else {
            // If no matching pnc found, then pick the PNCS object which has the closest PNCS["s"] date to the BasisDate
            // (without being later than the BasisDate).

            const earlier = stream.portfolioValues.filter(
                (p) => p.previousNightCloseDate <= component.basisDay!.getTime()
            );
            if (earlier.length > 0) {
                ret = pncToBase(earlier[earlier.length - 1]);
            } else {
                //If there are no PNCS objects earlier than the BasisDate then just pick the earliest PNCS object.
                ret = pncToBase(sortedPncs[0]);
            }
        }
    } else {
        const basisDay = isNil(component.basisDay)
            ? // If BasisTime is set and BasisDate is not set
              // Set the BasisDate to the latest available BasisDate user settings date option, and then follow the logic below.
              availableBasisDays[availableBasisDays.length - 1]
            : component.basisDay;

        // If BasisTime and BasisDate are both set,

        if (stream.initPortfolioValues) {
            // Initially set the p1-p5 values to the corresponding p1-p5 value in the INITVAL object.
            ret = initValuesToBase(stream.initPortfolioValues);
        }

        const basisDateTime = toDateTime(basisDay, component.basisTime);

        // Then to calculate the final value for each p1-p5, try to locate the latest minute bucket in the main timeseries array
        // that is not later than the date and time specified by BasisDate and BasisTime,
        // and also contains the corresponding p1-p5 value (i.e. has a buyValue)

        const earlier = stream.timeSeries.filter(
            (t) => t._i * 60000 <= basisDateTime.getTime() && !isNil(t.buyValue)
        );

        if (earlier.length > 0) {
            const item = earlier[earlier.length - 1];
            // Then set the p1-p5 value to the corresponding p1-p5 value in the main timeseries array minute bucket thus located.
            ret = {
                referenceSource: 'TimeSeries',
                time: item._i * 60000,
                buy: item.buyValue,
                sell: item.sellValue,
                short: item.shortValue,
                index1: item.priceIndex1,
                index2: item.priceIndex2,
            };
        }

        // If no minute bucket was found satisfying these criteria, leave the corresponding p1-p5 value with the value set initially using INITVAL.
    }

    if (!ret) {
        return undefined;
    }

    if (!isNil(ret.buy) || !isNil(ret.sell) || !isNil(ret.short)) {
        ret.totalGross = (ret.buy ?? 0) + (ret.sell ?? 0) + (ret.short ?? 0);
        ret.totalNet = (ret.buy ?? 0) - (ret.sell ?? 0) - (ret.short ?? 0);
    }

    return ret;
}

function mapTimeSeries(
    stream: PortfolioReturnsDataState,
    baseValues: PortfolioReturnsReferenceDataItem,
    showBuysAsPositive: boolean,
    invertSellLine: boolean
): PortfolioReturnsDataItem[] {
    const sortedTimeSeries = sortBy(stream.timeSeries, '_i');
    const ret: PortfolioReturnsDataItem[] = [];

    let prevBuyValue: number | undefined = undefined;
    let prevSellValue: number | undefined = undefined;
    let prevShortValue: number | undefined = undefined;

    for (const item of sortedTimeSeries) {
        const buyValueChange = getValueChange(item.buyValue, baseValues.buy);
        const sellValueChange = getValueChange(
            item.sellValue,
            baseValues.sell,
            invertSellLine ? 1 : -1
        );

        const shortValueChange = getValueChange(
            item.shortValue,
            baseValues.short,
            invertSellLine ? 1 : -1
        );
        const index1ValueChange = getValueChange(
            item.priceIndex1,
            baseValues.index1
        );
        const index2ValueChange = getValueChange(
            item.priceIndex2,
            baseValues.index2
        );

        let totalNet: number | undefined;
        let totalValueChange: number | undefined;
        let totalBps: number | undefined;

        const buyValueForTotalCalc = isNil(item.buyValue)
            ? prevBuyValue
            : item.buyValue;
        const absSellValueForTotalCalc = isNil(item.sellValue)
            ? prevSellValue
            : item.sellValue;
        const absShortValueForTotalCalc = isNil(item.shortValue)
            ? prevShortValue
            : item.shortValue;
        if (
            !isNil(buyValueForTotalCalc) ||
            !isNil(absSellValueForTotalCalc) ||
            !isNil(absShortValueForTotalCalc)
        ) {
            totalNet =
                (buyValueForTotalCalc ?? 0) -
                (absSellValueForTotalCalc ?? 0) -
                (absShortValueForTotalCalc ?? 0);

            const baseTotalGross =
                (baseValues.buy ?? 0) +
                (baseValues.sell ?? 0) +
                (baseValues.short ?? 0);
            const baseTotalNet =
                (baseValues.buy ?? 0) -
                (baseValues.sell ?? 0) -
                (baseValues.short ?? 0);
            totalValueChange = totalNet - (baseValues.totalNet ?? 0);
            totalBps = baseTotalGross
                ? calculateBps(totalValueChange, baseTotalGross)
                : calculateBps(totalValueChange, baseTotalNet);
        }

        const maxAbsoluteValueChange = calcMaxAbsoluteValueChange(
            buyValueChange,
            sellValueChange,
            shortValueChange,
            totalValueChange,
            index1ValueChange,
            index2ValueChange
        );

        const dataItem = {
            time: item._i * 60000,
            endOfBucketTime: item._i * 60000 + 60000,
            totalBpsValue: totalBps,
            buyBpsValue: calculateBps(buyValueChange, baseValues.buy),
            sellBpsValue: calculateBps(sellValueChange, baseValues.sell),
            shortBpsValue: calculateBps(shortValueChange, baseValues.short),
            index1BpsValue: calculateBps(index1ValueChange, baseValues.index1),
            index2BpsValue: calculateBps(index2ValueChange, baseValues.index2),
            totalValueChange,
            buyValueChange,
            sellValueChange,
            shortValueChange,
            index1ValueChange,
            index2ValueChange,
            maxAbsoluteValueChange,
            totalNet,
            ...item,
        };

        applySideInverse(dataItem, showBuysAsPositive);

        ret.push(dataItem);

        if (!isNil(item.buyValue)) {
            prevBuyValue = item.buyValue;
        }
        if (!isNil(item.sellValue)) {
            prevSellValue = item.sellValue;
        }
        if (!isNil(item.shortValue)) {
            prevShortValue = item.shortValue;
        }
    }
    return ret;
}

function getValueChange(
    value: number | undefined,
    baseValue: number | undefined,
    factor = 1
): number | undefined {
    if (isNil(value)) {
        return undefined;
    }
    return factor * (value - (baseValue ?? 0));
}

function calculateBps(
    valueChange: number | undefined,
    baseValue: number | undefined
): number | undefined {
    if (baseValue && valueChange !== undefined) {
        return (10000 * valueChange) / baseValue;
    }
}

function calcMaxAbsoluteValueChange(
    ...values: (number | undefined)[]
): number | undefined {
    let ret: number | undefined;

    for (const value of values) {
        if (!isNil(value)) {
            const absValue = Math.abs(value);
            ret = ret === undefined ? absValue : Math.max(ret, absValue);
        }
    }

    return ret;
}

export function getYDomain(
    lines: (keyof PortfolioReturnsDataItem)[],
    timeSeries: PortfolioReturnsDataItem[]
) {
    const accessors = lines.map((l) => (d: PortfolioReturnsDataItem) => d[l]);
    const d =
        timeSeries.length &&
        extentLinear().accessors(accessors).pad([0.1, 0.1])(timeSeries);
    if (Array.isArray(d) && d.length === 2) {
        if (d[0] === d[1]) {
            d[0] = d[0] * 0.8;
            d[1] = d[1] * 1.2;
        }
    }
    return d;
}

function applySideInverse(
    dataItem: PortfolioReturnsDataItem,
    showBuysAsPositive: boolean
) {
    const flip = (v: number | undefined) => (v === undefined ? v : v * -1);

    if (showBuysAsPositive) {
        dataItem.buyValueChange = flip(dataItem.buyValueChange);
        dataItem.buyBpsValue = flip(dataItem.buyBpsValue);
        dataItem.sellValueChange = flip(dataItem.sellValueChange);
        dataItem.sellBpsValue = flip(dataItem.sellBpsValue);
        dataItem.shortValueChange = flip(dataItem.shortValueChange);
        dataItem.shortBpsValue = flip(dataItem.shortBpsValue);
        dataItem.totalValueChange = flip(dataItem.totalValueChange);
        dataItem.totalBpsValue = flip(dataItem.totalBpsValue);
    }
}
